// Unified hook for all game element operations - turns, prompts, answers, votes
// Provides both generic operations and specific functionality

import { useCallback } from 'react';
import { useRoomStateSelector } from './useRoomStateSelector';
import { useRoomInteractionWindow } from './useRoomInteractionWindow';
import { useRoomMyPlayer } from './useRoomMyPlayer';
import { getLatestTurnAssignments as getLatestTurnAssignmentsEngine } from '@airhead-games/shared/engine/roomState';
import { GameElementType, GameElement } from '@airhead-games/shared/types';
import {
  getGameElementCollectionKey,
  type GameElementCollectionKey,
  parseElementId,
} from '@airhead-games/shared/utilities';

interface FilterOptions {
  parentId?: string;
  contentGroup?: string;
  assignedPlayerId?: string;
  content?: string;
  elementIds?: string[];
}

interface PlayerCategorizedElements {
  mine: GameElement[];
  others: GameElement[];
  all: GameElement[];
}

export function useRoomGameElements() {
  // --- Data Access ---
  const gameElements = useRoomStateSelector((state) => ({
    turns: state?.turns ?? {},
    prompts: state?.prompts ?? {},
    answers: state?.answers ?? {},
    votes: state?.votes ?? {},
  }));

  const myPlayer = useRoomMyPlayer();
  const { windowId, windowType } = useRoomInteractionWindow();

  // =============================================================================
  // GENERIC OPERATIONS
  // =============================================================================
  // Reusable functions that work across all game element types

  /**
   * Get elements of a specific type with optional filtering
   * @param type - The element type to get
   * @param filters - Optional filters to apply
   * @returns Array of elements matching the criteria
   */
  const getElements = useCallback(
    (type: GameElementCollectionKey, filters: FilterOptions = {}) => {
      const elements = Object.values(gameElements[type]);

      return elements.filter((element) => {
        // Filter by parentId
        if (
          filters.parentId !== undefined &&
          element.parentId !== filters.parentId
        ) {
          return false;
        }

        // Filter by contentGroup
        if (
          filters.contentGroup !== undefined &&
          element.contentGroup !== filters.contentGroup
        ) {
          return false;
        }

        // Filter by assignedPlayerId
        if (
          filters.assignedPlayerId !== undefined &&
          element.assignedPlayerId !== filters.assignedPlayerId
        ) {
          return false;
        }

        // Filter by content
        if (
          filters.content !== undefined &&
          element.content !== filters.content
        ) {
          return false;
        }

        // Filter by elementIds
        if (
          filters.elementIds !== undefined &&
          !filters.elementIds.includes(element.elementId)
        ) {
          return false;
        }

        return true;
      });
    },
    [gameElements]
  );

  /**
   * Get a game element by ID without requiring callers to know its collection.
   * @param id - The element ID to look up
   * @returns The matching element, or undefined if the ID is invalid or missing from state
   */
  const getElementById = useCallback(
    (id: string): GameElement | undefined => {
      const parsed = parseElementId(id);
      if (!parsed) return undefined;

      const collection = getGameElementCollectionKey(parsed.elementType);
      if (!collection) return undefined;

      return gameElements[collection][id];
    },
    [gameElements]
  );

  /**
   * Get child elements for a specific parent
   * @param parentId - The parent element ID
   * @param childType - The type of child elements to get
   * @param filters - Optional additional filters
   * @returns Array of child elements
   */
  const getChildElements = useCallback(
    (
      parentId: string,
      childType: GameElementCollectionKey,
      filters: FilterOptions = {}
    ) => {
      return getElements(childType, { ...filters, parentId });
    },
    [getElements]
  );

  /**
   * Categorize elements by player ownership
   * @param elements - Elements to categorize
   * @param playerId - The player ID to use for categorization
   * @returns Object with mine, others, and all arrays
   */
  const categorizeByPlayer = useCallback(
    (elements: GameElement[], playerId: string): PlayerCategorizedElements => {
      const mine = elements.filter((el) => el.assignedPlayerId === playerId);
      const others = elements.filter(
        (el) => el.assignedPlayerId !== playerId && el.assignedPlayerId
      );
      return { mine, others, all: elements };
    },
    []
  );

  /**
   * Group elements by a specific field
   * @param elements - Elements to group
   * @param field - Field to group by
   * @returns Map of field values to element arrays
   */
  const groupElementsBy = useCallback(
    (elements: GameElement[], field: keyof GameElement) => {
      const groups = new Map<GameElement[keyof GameElement], GameElement[]>();

      elements.forEach((element) => {
        const value = element[field];
        if (value !== undefined && value !== null && value !== '') {
          let group = groups.get(value);
          if (!group) {
            group = [];
            groups.set(value, group);
          }
          group.push(element);
        }
      });

      return groups;
    },
    []
  );

  /**
   * Get one representative element for each unique content group
   * @param elements - Elements to process
   * @returns Array of representative elements
   */
  const getRepresentativeByContentGroup = useCallback(
    (elements: GameElement[]) => {
      const representatives = new Map<string, GameElement>();

      elements.forEach((element) => {
        const contentGroup =
          typeof element.contentGroup === 'string' && element.contentGroup;
        if (contentGroup && !representatives.has(contentGroup)) {
          representatives.set(contentGroup, element);
        }
      });

      return Array.from(representatives.values());
    },
    []
  );

  // =============================================================================
  // TURN OPERATIONS
  // =============================================================================
  // Turn navigation and state determination

  /**
   * Get the latest turn element (last in sequential order)
   * @returns The latest TurnElement or undefined if none found
   */
  const getLatestTurn = useCallback(() => {
    const turns = getElements('turns');
    return turns.length > 0 ? turns[turns.length - 1] : undefined;
  }, [getElements]);

  const latestTurn = getLatestTurn();

  /**
   * Get the turn element that is currently in progress
   * @returns The turn element in progress or null if none
   */
  const getTurnInProgress = useCallback(() => {
    if (!windowId) {
      return null;
    }

    if (windowType === GameElementType.TURN) {
      return latestTurn || null;
    }

    // If we are in window other than TURN...
    if (latestTurn) {
      const hasPrompts = latestTurn.promptIds?.length > 0;
      const hasContent = latestTurn.content !== '';
      const hasLatestTurnStarted = hasPrompts || hasContent;

      if (hasLatestTurnStarted) {
        return latestTurn;
      } else {
        const turns = getElements('turns');
        const previousTurn = turns[turns.length - 2];
        return previousTurn || null;
      }
    }

    return null;
  }, [windowId, windowType, latestTurn, getElements]);

  const turnInProgress = getTurnInProgress();

  // =============================================================================
  // ASSIGNMENT OPERATIONS
  // =============================================================================
  // Element assignment and categorization

  /**
   * Get latest turn assignments categorized by player
   * @returns Assignment data with mine/others/all categorization
   */
  const getLatestTurnAssignments = useCallback(() => {
    if (!myPlayer?.playerId) {
      return {
        turn: undefined,
        promptsMine: [],
        promptsOthers: [],
        promptsAll: [],
        answersMine: [],
        answersOthers: [],
        answersAll: [],
        votesMine: [],
        votesOthers: [],
        votesAll: [],
      };
    }

    return getLatestTurnAssignmentsEngine(
      gameElements.turns,
      gameElements.prompts,
      gameElements.answers,
      gameElements.votes,
      myPlayer.playerId
    );
  }, [gameElements, myPlayer]);

  const latestTurnAssignments = getLatestTurnAssignments();

  // =============================================================================
  // VOTING OPERATIONS
  // =============================================================================
  // Voting logic and answer management

  /**
   * Get the prompt ID for a given vote ID
   * @param voteId - The vote element ID
   * @returns The prompt ID or undefined
   */
  const getPromptIdForVote = useCallback(
    (voteId: string) => {
      const vote = gameElements.votes[voteId];
      if (!vote?.parentId) return undefined;

      const answer = gameElements.answers[vote.parentId];
      return answer?.parentId; // This is the promptId
    },
    [gameElements.votes, gameElements.answers]
  );

  /**
   * Get all vote elements for a given prompt ID
   * @param promptId - The prompt element ID
   * @returns Array of vote elements for that prompt
   */
  const getVoteIdsForPrompt = useCallback(
    (promptId: string) => {
      const answersForPrompt = getChildElements(promptId, 'answers');
      const votesForPrompt = answersForPrompt.flatMap((answer) =>
        getChildElements(answer.elementId, 'votes')
      );

      return votesForPrompt;
    },
    [getChildElements]
  );

  /**
   * Get my vote for a specific prompt
   * @param promptId - The prompt ID to check
   * @returns My vote for that prompt or undefined
   */
  const getMyVoteForPrompt = useCallback(
    (promptId: string) => {
      if (!myPlayer?.playerId) return undefined;

      const answersForPrompt = getChildElements(promptId, 'answers');
      const { mine: myVotes } = categorizeByPlayer(
        getElements('votes'),
        myPlayer.playerId
      );

      return myVotes.find((vote) =>
        answersForPrompt.some((ans) => ans.elementId === vote.parentId)
      );
    },
    [getChildElements, categorizeByPlayer, getElements, myPlayer]
  );

  /**
   * Get unique answers for a prompt (one per content group)
   * @param promptId - The prompt ID
   * @returns Array of representative answers
   */
  const getUniqueAnswersForPrompt = useCallback(
    (promptId: string) => {
      const answersForPrompt = getChildElements(promptId, 'answers');
      return getRepresentativeByContentGroup(answersForPrompt);
    },
    [getChildElements, getRepresentativeByContentGroup]
  );

  /**
   * Get answers that the current player can vote for
   * @param promptId - The prompt ID
   * @returns Array of voteable answers
   */
  const getVoteableAnswersForPrompt = useCallback(
    (promptId: string) => {
      if (!myPlayer?.playerId) return [];

      const uniqueAnswers = getUniqueAnswersForPrompt(promptId);
      const { mine: myAnswers } = categorizeByPlayer(
        getChildElements(promptId, 'answers'),
        myPlayer.playerId
      );

      const myContentGroup =
        typeof myAnswers[0]?.contentGroup === 'string' &&
        myAnswers[0].contentGroup;

      if (!myContentGroup) {
        return uniqueAnswers;
      }

      return uniqueAnswers.filter((answer) => {
        const contentGroup =
          typeof answer.contentGroup === 'string' && answer.contentGroup;
        return contentGroup !== myContentGroup;
      });
    },
    [getUniqueAnswersForPrompt, categorizeByPlayer, getChildElements, myPlayer]
  );

  /**
   * Get players who submitted answers in a specific content group
   * @param promptId - The prompt ID
   * @param winningContentGroup - The content group to check
   * @returns Array of player IDs
   */
  const getPlayersByAnswerContentGroup = useCallback(
    (promptId: string, winningContentGroup?: string) => {
      return getChildElements(promptId, 'answers', {
        contentGroup: winningContentGroup,
      })
        .map((ans) => ans.assignedPlayerId)
        .filter(Boolean) as string[];
    },
    [getChildElements]
  );

  /**
   * Get all votes for a specific answer
   * @param answerId - The answer ID
   * @returns Array of vote elements for that answer
   */
  const getVotesForAnswer = useCallback(
    (answerId: string) => {
      return getChildElements(answerId, 'votes');
    },
    [getChildElements]
  );

  /**
   * Get player IDs who voted for a specific answer
   * @param answerId - The answer ID
   * @returns Array of player IDs who voted for that answer
   */
  const getVoterPlayerIdsForAnswer = useCallback(
    (answerId: string) => {
      const votes = getVotesForAnswer(answerId);
      return votes
        .map((vote) => vote.assignedPlayerId)
        .filter(Boolean) as string[];
    },
    [getVotesForAnswer]
  );

  return {
    // Raw data access
    turns: gameElements.turns,
    prompts: gameElements.prompts,
    answers: gameElements.answers,
    votes: gameElements.votes,

    // Generic operations
    getElements,
    getElementById,
    getChildElements,
    categorizeByPlayer,
    groupElementsBy,
    getRepresentativeByContentGroup,

    // Turn operations
    getLatestTurn,
    latestTurn,
    getTurnInProgress,
    turnInProgress,

    // Assignment operations
    getLatestTurnAssignments,
    turn: latestTurnAssignments.turn,
    promptsMine: latestTurnAssignments.promptsMine,
    promptsOthers: latestTurnAssignments.promptsOthers,
    promptsAll: latestTurnAssignments.promptsAll,
    answersMine: latestTurnAssignments.answersMine,
    answersOthers: latestTurnAssignments.answersOthers,
    answersAll: latestTurnAssignments.answersAll,
    votesMine: latestTurnAssignments.votesMine,
    votesOthers: latestTurnAssignments.votesOthers,
    votesAll: latestTurnAssignments.votesAll,

    // Voting operations
    getPromptIdForVote,
    getVoteIdsForPrompt,
    getMyVoteForPrompt,
    getUniqueAnswersForPrompt,
    getVoteableAnswersForPrompt,
    getPlayersByAnswerContentGroup,
    getVotesForAnswer,
    getVoterPlayerIdsForAnswer,
  };
}
